<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { useNowTick } from '@/stores/nowTick.store';
import { useTaskHub } from '@/composables/useTaskHub';
import { useDueResolveTrigger } from '@/composables/useDueResolveTrigger';
import { useAsyncLoad } from '@/composables/useAsyncLoad';
import * as researchApi from '@/api/research';
import type { ResearchInProgressItemView, ResearchNodeView, ResearchTreeView } from '@/types/research';
import ResearchTree from '@/components/research/ResearchTree.vue';
import ResearchNode from '@/components/research/ResearchNode.vue';

/**
 * Vue lecture-seule de l'arbre de recherche au niveau GLOBAL du joueur-univers
 * (wiki §24.7-bis). Affiche le niveau maximum atteint sur n'importe quelle
 * planete pour chaque tech + les liaisons de prerequis. Pour DEMARRER une
 * recherche il faut passer par la vue planetaire (ResearchView).
 */
const auth = useAuthStore();
const session = useSessionStore();
const tree = ref<ResearchTreeView | null>(null);
const inProgress = ref<ResearchInProgressItemView[]>([]);

// Tier 2 : resolve-mine pile à l'échéance de la recherche en cours la plus proche.
useDueResolveTrigger(() => {
  let min: number | null = null;
  for (const it of inProgress.value) {
    const t = new Date(it.finishAt).getTime();
    if (min === null || t < min) min = t;
  }
  return min;
});
const { loading, run } = useAsyncLoad({ errorPrefix: 'Recherche :' });
const activeCategory = ref<string>('');
const selectedId = ref<number | null>(null);

const { now: nowMs } = useNowTick().useTick();
const progressById = computed<Record<number, number>>(() => {
  const out: Record<number, number> = {};
  for (const it of inProgress.value) {
    const start = new Date(it.startedAt).getTime();
    const end = new Date(it.finishAt).getTime();
    out[it.researchDefinitionId] = end <= start
      ? 100
      : Math.min(100, Math.max(0, Math.round(((nowMs.value - start) / (end - start)) * 100)));
  }
  return out;
});

const selectedNode = computed<ResearchNodeView | null>(() => {
  if (selectedId.value == null) return null;
  for (const cat of tree.value?.categories ?? []) {
    const found = cat.researches.find((n) => n.id === selectedId.value);
    if (found) return found;
  }
  return null;
});

async function load(): Promise<void> {
  if (!auth.user) return;
  await run(async () => {
    // Pas de planetId -> CurrentLevel = niveau GLOBAL du joueur.
    const [t, ip] = await Promise.all([
      researchApi.fetchResearchTree(session.activePlayerUniverseId ?? 0),
      researchApi.fetchResearchInProgress(session.activePlayerUniverseId ?? 0),
    ]);
    tree.value = t;
    inProgress.value = ip.items;
    if (t.categories.length && !activeCategory.value) {
      activeCategory.value = t.categories[0].code;
    }
  });
}

// Push temps-réel : une recherche résolue par le tick -> on recharge l'arbre +
// les "en cours" (le panel flippe déjà en « Finalisation… » à 0s côté client).
const taskHub = useTaskHub();
let taskUnsub: (() => void) | null = null;
onMounted(() => {
  void load();
  taskUnsub = taskHub.onTaskResolved((p) => {
    if (p.taskType === 'Research') void load();
  });
});
onUnmounted(() => { if (taskUnsub) { taskUnsub(); taskUnsub = null; } });
</script>

<template>
  <div v-loading="loading" class="research-global-view">
    <header class="research-header">
      <div>
        <h1 class="research-title">Arbre technologique global</h1>
        <p class="text-muted">
          Niveau max atteint par le joueur sur l'ensemble de ses planètes.
          Pour lancer une recherche, ouvrez la vue <router-link to="/research">Recherches (planète)</router-link>.
        </p>
      </div>
    </header>

    <div class="research-layout">
      <div class="research-main">
        <el-tabs v-model="activeCategory" class="research-tabs">
          <el-tab-pane
            v-for="cat in tree?.categories ?? []"
            :key="cat.code"
            :label="cat.name"
            :name="cat.code"
          >
            <ResearchTree
              :researches="cat.researches"
              :selected-id="selectedId"
              :progress-by-id="progressById"
              @select="(n) => (selectedId = n.id)"
            />
          </el-tab-pane>
        </el-tabs>

        <el-empty
          v-if="!loading && (!tree || tree.categories.length === 0)"
          description="Aucune recherche disponible."
        />
      </div>

      <aside class="research-aside">
        <ResearchNode v-if="selectedNode" :node="selectedNode" readonly />
        <div v-else class="research-hint">
          <el-icon><Pointer /></el-icon>
          <span class="text-muted text-sm">Sélectionne une recherche pour voir son détail.</span>
        </div>
      </aside>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.research-global-view {
  max-width: $layout-max-width;
}
.research-header {
  margin-bottom: $space-6;
}
.research-title {
  font-family: $font-display;
  font-size: $font-size-3xl;
  margin: 0 0 $space-1;
}
.research-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-6;
  @include breakpoint(lg) {
    grid-template-columns: 1fr 320px;
  }
}
// min-width:0 : sans ça le 1fr prend la largeur INTRINSÈQUE de l'arbre déroulé
// (min-width:auto par défaut sur un item grid) -> la page déborde et une 2e
// scrollbar horizontale apparaît. Avec 0, la colonne reste dans sa piste et
// .rt-scroll gère seul le défilement de l'arbre.
.research-main { min-width: 0; }
.research-aside {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}
.research-hint {
  @include card-dark;
  padding: $space-4;
  display: flex;
  align-items: center;
  gap: $space-2;
  .el-icon { color: $color-neon-cyan; font-size: 20px; flex-shrink: 0; }
}
</style>
